/* External */
import {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useIntl } from 'react-intl';
import { List } from 'immutable';

/* Theme default card artwork (the same import the old MiniEpgDetailCard uses). */
import defaultGuideImage from '~/assets/theme/images/defaultImageMiniEpgDetailCard.png';

/* Hooks */
import useEpgFilters from './useEpgFilters';
import useSelectedEpgFilter from './useSelectedEpgFilter';
import useSortAndFilterChannels from './useSortAndFilterChannels';
import useMultipleRequests from './useMultipleRequests';

/* Storage */
import { scheduleFetchChannelSchedules } from '../storage/channels';
import { selectAllSchedules } from '../storage/schedules';
import { selectAllContents } from '../storage/contents';
import { deleteMultipleRequests } from '../storage/requests';

/* Utils */
import {
  DOT_SEPARATOR,
  EPG_FILTERS_ID,
  ImageRatio,
  PROVIDERS,
  STATUS_TYPES,
} from '../utils/constants';
import { getImage } from '../utils/imageUtils';
import {
  isEpisode,
  isReplay,
  joinArrayWithSeparator,
} from '../utils/metaDataUtils';
import { redirectToPlayer } from '../utils/history';
import { PLAY_SOURCE_PAGE, PLAY_SOURCE_SECTION } from '../utils/trackSourceConviva';

/* Lego mini EPG (guide) — the React data layer.
 *
 * WHY A HOOK AND NOT COMPONENTS: the Solid lego player must never import
 * app-owned modules (Redux, react-intl, Immutable, services) — the solid TS
 * program would claim React files and trip TS6307. So the shell resolves
 * everything into plain objects/strings and hands the lego a small, ref-backed
 * API (the `guide` option of mountLegoPlayer).
 *
 * PERFORMANCE CONTRACT (the lego exists because old TVs are slow):
 *  - nothing is fetched until the guide is actually opened (`enabled`); the
 *    For-You carousels / channel self-heal start only then;
 *  - schedules are fetched ONLY for the channel window the user can reach
 *    (onRange), never for the whole channel list upfront — the old mini EPG
 *    dispatched a batch for every channel at once (200 channels × the saga's
 *    5-channel batch = 40 sequential requests before the panel was usable);
 *  - "what's on now" is one `schedulesForChannel: 1` batch, re-requested on a
 *    30s bucket when the loaded program has ended;
 *  - rows are plain objects rebuilt only when the store or the "now" bucket
 *    changes (never per tick), with a channel-logo cache so getImage() is not
 *    re-run per channel on every rebuild;
 *  - NO Intl date formatting per row (the heaviest per-row cost on webOS 3):
 *    the solid side formats HH:MM from the epoch it already has, exactly like
 *    its "A SEGUIR" card.
 */

/* Channel logo request size — the old MiniScheduleCard asked for 80px wide. */
const CHANNEL_LOGO_WIDTH = 80;
const CHANNEL_LOGO_RATIO = '4:3';

/* Detail-block artwork: the old MiniEpgDetailCard asked for 576px at 16:9 and
   fell back to the theme's default card image. */
const GUIDE_IMAGE_WIDTH = 576;
const GUIDE_IMAGE_RATIO = ImageRatio.SIXTEEN_BY_NINE;

/* How often the "what's on now" resolution is re-evaluated while the guide is
   open (a program boundary with the panel up must not freeze the rows). */
const GUIDE_REFRESH_MS = 30_000;

/* First window requested on open; the list asks for more via onRange. */
const GUIDE_INITIAL_RANGE = 24;

/* One retry of a dispatch the schedules saga may have swallowed inside its 1s
   throttle window (see the effect below). */
const GUIDE_DISPATCH_RETRY_MS = 1400;

const contentIdHasSuffix = /^[\dA-Fa-f-]{36}.+/;
const contentIdUuid = /^[\dA-Fa-f-]{36}/;

/* `schedule.startTime`/`endTime` are Date objects (typed-normalizr `date()`). */
function toMs(value) {
  if (typeof value === 'number') return value;
  return value ? new Date(value).getTime() : Number.NaN;
}

/* Resolve the schedule entities a channel points at. The ids in
   `channel.schedules` ARE the normalized entity ids (provider suffix already
   included) — resolve RAW first and only then try the provider suffix, exactly
   like useChannelSchedules (appending the provider again misses every suffixed
   id and silently drops the program). */
function resolveChannelSchedules(channel, scheduleEntities) {
  const ids = channel?.schedules;
  if (!ids || ids.size === 0) return [];
  const provider = channel?.sourceProvider ?? PROVIDERS.VRIO;
  const resolved = [];
  /* eslint-disable-next-line unicorn/no-array-for-each
      -- channel.schedules is an Immutable List; its .forEach is the fast
      internal iteration (no iterator-protocol overhead) on webOS 3/4. */
  ids.forEach((scheduleId) => {
    const id = String(scheduleId);
    const schedule = scheduleEntities.get(id) ?? scheduleEntities.get(`${id}-${provider}`);
    if (schedule) resolved.push(schedule);
  });
  return resolved;
}

/* The program on air now; when none matches (gap / stale window) the next one
   that starts in the future, then the last loaded one — a row with data is
   never empty. */
function pickCurrentSchedule(schedules, nowMs) {
  let next;
  let last;
  let lastEnd = Number.NEGATIVE_INFINITY;
  for (const schedule of schedules) {
    const start = toMs(schedule?.startTime);
    const end = toMs(schedule?.endTime);
    if (!Number.isFinite(start) || !Number.isFinite(end)) continue;
    if (start <= nowMs && nowMs < end) return schedule;
    if (start > nowMs && (next === undefined || start < toMs(next.startTime))) next = schedule;
    if (end > lastEnd) {
      last = schedule;
      lastEnd = end;
    }
  }
  return next ?? last;
}

function formatContentId(contentId, provider) {
  if (!contentId) return '';
  const value = String(contentId);
  if (contentIdHasSuffix.test(value)) return value;
  const clean = value.match(contentIdUuid)?.[0] ?? value;
  return `${clean}-${provider}`;
}

/* Port of the old player's useTitle: the content title, inverted for an episode
   with a number, prefixed when the schedule is a replay. */
function resolveTitle(content, schedule, formatMessage) {
  let title = content?.title || schedule?.title || '';
  if (isEpisode(content) && content?.episode?.number) {
    title = content?.episode?.showName || title;
  }
  if (isReplay(schedule)) {
    return `${formatMessage({ id: 'common.replay' })}: ${title}`;
  }
  return title;
}

/* Port of the old player's useFullEpisodeName (the detail card's second line). */
function resolveEpisodeName(content, formatMessage) {
  if (!isEpisode(content)) return '';
  const { episode } = content || {};
  if (!episode || ((episode.number ?? 0) <= 0 && content.title !== episode.showName)) {
    return joinArrayWithSeparator([
      episode?.showName,
      joinArrayWithSeparator((content?.genres ?? [])?.slice?.(0, 3)),
    ]);
  }
  if (content.title === episode.showName) {
    return joinArrayWithSeparator((content?.genres ?? [])?.slice?.(0, 3));
  }
  const fullNumber = [
    episode.seasonNumber
      ? `${formatMessage({ id: 'common.seasonFirstChar' })}${episode.seasonNumber}`
      : '',
    episode.number
      ? `${formatMessage({ id: 'common.episodeFirstChar' })}${episode.number}`
      : '',
  ].filter(Boolean).join(' ');
  return joinArrayWithSeparator([fullNumber, content.title], DOT_SEPARATOR);
}

function buildChannelRow(channel, context) {
  const {
    nowMs, scheduleEntities, contentEntities, formatMessage, logoUrl, contentImage,
    isCurrentChannel,
  } = context;
  const schedule = pickCurrentSchedule(
    resolveChannelSchedules(channel, scheduleEntities),
    nowMs,
  );
  const provider = channel?.sourceProvider ?? PROVIDERS.VRIO;
  const content = contentEntities.get(formatContentId(schedule?.content, provider));
  return {
    channelId: channel.channelId,
    channelName: channel.title || channel.channelName || '',
    channelLogo: logoUrl,
    image: contentImage(content),
    title: resolveTitle(content, schedule, formatMessage),
    episode: resolveEpisodeName(content, formatMessage),
    rating: content?.rating || '',
    startMs: toMs(schedule?.startTime),
    endMs: toMs(schedule?.endTime),
    isCurrentChannel,
    isBlackout: !!schedule?.blackout,
    hasSchedule: !!schedule,
  };
}

/* Channels whose "now" program is not usable yet: never fetched, or the loaded
   program already ended (the 30s bucket re-checks the second case). Read off the
   built rows, which already carry the resolved window. */
function needsScheduleFetch(row, nowMs) {
  return !row.hasSchedule || row.endMs <= nowMs;
}

function sameNumber(a, b) {
  return a === b || (Number.isNaN(a) && Number.isNaN(b));
}

/* Identity reconciliation, the same idea as the tracks panel's
   reconcilePanelTracks: the lego renders the rows with Solid's keyed `For`, so
   handing it a fresh object per channel on every store change would recreate
   every visible row (and its focus registration) even when only ONE channel's
   program changed — 40 schedule batches landing while the guide is open would
   mean 40 full rebuilds of the strip. Keep the previous object whenever every
   display field is unchanged. */
function sameGuideRow(previous, next) {
  return previous.channelName === next.channelName
    && previous.channelLogo === next.channelLogo
    && previous.image === next.image
    && previous.title === next.title
    && previous.episode === next.episode
    && previous.rating === next.rating
    && sameNumber(previous.startMs, next.startMs)
    && sameNumber(previous.endMs, next.endMs)
    && previous.isCurrentChannel === next.isCurrentChannel
    && previous.isBlackout === next.isBlackout
    && previous.hasSchedule === next.hasSchedule;
}

export default function useLegoMiniEpg({ isLive, currentChannelId, homeOnBack }) {
  const { formatMessage } = useIntl();
  const dispatch = useDispatch();
  const scheduleEntities = useSelector(selectAllSchedules);
  const contentEntities = useSelector(selectAllContents);
  /* The route marks a mini-EPG entry with homeOnBack=true (the old player's
     redirectToPlayer query) so BACK leaves the player for Home. Query params
     arrive as strings; anything else (absent / 'false') means "no override". */
  const wantsHomeOnBack = String(homeOnBack ?? '') === 'true';

  /* Armed by the lego the first time the guide panel is opened: nothing below
     fetches anything until then. `visible` only gates the "now" refresh tick —
     the resolved rows stay cached, so reopening is instant. */
  const [enabled, setEnabled] = useState(false);
  const [visible, setVisible] = useState(false);
  /* Highest channel index the lego asked for (visible window + lookahead). */
  const [rangeEnd, setRangeEnd] = useState(GUIDE_INITIAL_RANGE);
  const [nowBucket, setNowBucket] = useState(() => Date.now());
  const [requestIds, setRequestIds] = useState(() => List());
  const [dispatchTick, setDispatchTick] = useState(0);
  const requestIdsRef = useRef(requestIds);
  requestIdsRef.current = requestIds;
  const requests = useMultipleRequests(requestIds);
  const isFetchingSchedules = useMemo(
    () => requests.some(({ status }) => status === STATUS_TYPES.INIT_STATE),
    [requests],
  );

  const { filtersMiniEpg } = useEpgFilters();
  const {
    channels,
    selectedFilter,
    handleFilterSelect,
    isFetching,
  } = useSelectedEpgFilter({
    filters: filtersMiniEpg,
    defaultFilter: EPG_FILTERS_ID.FOR_YOU,
    isMiniEpgFilters: true,
    readyToFetch: enabled && isLive,
  });

  /* Nothing below does any work before the guide is opened: the sort/filter of
     the whole channel list is skipped entirely until then (the lego player is
     also used for VOD, where the guide never opens at all). */
  const sortedChannels = useSortAndFilterChannels(
    enabled ? channels : undefined,
    selectedFilter?.id === EPG_FILTERS_ID.FOR_YOU,
  );
  const channelList = useMemo(() => sortedChannels?.toList(), [sortedChannels]);
  const channelCount = channelList?.size ?? 0;

  /* Logo cache: getImage() walks the images collection and builds fallbacks, so
     the URL only has to change when the channel's images reference does. */
  const logoCacheRef = useRef(new Map());
  const channelLogo = useCallback((channel) => {
    const cache = logoCacheRef.current;
    const cached = cache.get(channel.channelId);
    if (cached && cached.images === channel.images) return cached.logo;
    const logo = getImage(channel.images, CHANNEL_LOGO_WIDTH, CHANNEL_LOGO_RATIO) || '';
    cache.set(channel.channelId, { images: channel.images, logo });
    return logo;
  }, []);

  /* Detail artwork cache (keyed by the images collection itself): getImage's
     ratio fallback walk is not free and the same content is rebuilt on every
     store change. The theme default is used when the content has no image —
     same as the old MiniEpgDetailCard. */
  const imageCacheRef = useRef(new WeakMap());
  const contentImage = useCallback((content) => {
    const images = content?.images;
    if (!images) return defaultGuideImage;
    const cache = imageCacheRef.current;
    const cached = cache.get(images);
    if (cached) return cached;
    const image = getImage(images, GUIDE_IMAGE_WIDTH, GUIDE_IMAGE_RATIO) || defaultGuideImage;
    cache.set(images, image);
    return image;
  }, []);

  /* Rows are rebuilt only on a store change or on the "now" bucket, and the
     objects of unchanged channels are REUSED (see sameGuideRow). Deliberately
     NOT keyed on `rangeEnd`: the lego widens the requested range as it scrolls,
     and rebuilding every row there would hand the panel a new object per
     channel (its windowed `For` is reference-keyed, so the whole visible strip
     would be recreated on every scroll step). */
  const previousRowsRef = useRef(new Map());
  const rows = useMemo(() => {
    if (!channelList || channelList.size === 0) {
      previousRowsRef.current = new Map();
      return [];
    }
    /* Deduped: a duplicate channel would collide on the solid focus key (the
       FocusManager throws) and take the whole panel down. */
    const previousRows = previousRowsRef.current;
    const currentRows = new Map();
    const seen = new Set();
    const built = [];
    for (let index = 0; index < channelList.size; index += 1) {
      const channel = channelList.get(index);
      // eslint-disable-next-line no-continue -- dedupe by channelId, see above
      if (!channel || seen.has(channel.channelId)) continue;
      seen.add(channel.channelId);
      const candidate = buildChannelRow(channel, {
        nowMs: nowBucket,
        scheduleEntities,
        contentEntities,
        formatMessage,
        logoUrl: channelLogo(channel),
        contentImage,
        isCurrentChannel: channel.channelId === currentChannelId,
      });
      const previous = previousRows.get(candidate.channelId);
      const row = previous && sameGuideRow(previous, candidate) ? previous : candidate;
      currentRows.set(row.channelId, row);
      built.push(row);
    }
    previousRowsRef.current = currentRows;
    return built;
  }, [
    channelList,
    nowBucket,
    scheduleEntities,
    contentEntities,
    formatMessage,
    channelLogo,
    contentImage,
    currentChannelId,
  ]);

  /* Only the requested window may request schedules (the lego reports how far
     its list window can reach). `pendingKey` doubles as the value identity of
     the pending list — a fresh array every recompute would re-fire the dispatch
     effect on every render. */
  const pendingKey = useMemo(() => {
    if (!enabled || rows.length === 0) return '';
    const limit = Math.min(rangeEnd, rows.length);
    const pending = [];
    for (let index = 0; index < limit; index += 1) {
      const row = rows[index];
      if (needsScheduleFetch(row, nowBucket)) pending.push(row.channelId);
    }
    return pending.join(',');
  }, [enabled, rangeEnd, rows, nowBucket]);

  /* One batched dispatch per change: the schedules saga throttles 1s and merges
     the ids of a single action, so a per-channel loop would lose all but the
     first. `pendingKey` is the value identity of the pending list (a fresh
     array every rebuild). */
  useEffect(() => {
    if (!enabled || pendingKey === '') return;
    const action = scheduleFetchChannelSchedules({
      channelIdOrIds: pendingKey.split(','),
      schedulesForChannel: 1,
    });
    setRequestIds((current) => current.push(action.requestId));
    dispatch(action);
  }, [enabled, pendingKey, dispatchTick, dispatch]);

  /* The saga's throttle drops a FETCH action that lands inside its 1s window,
     merging the request ids into the NEXT batch — which may never be triggered
     while the pending set does not change. One retry per distinct pending set
     keeps the guide from sitting on skeletons forever; bounded (a channel with
     genuinely no program is retried once, not in a loop). */
  const retriedKeysRef = useRef(new Set());
  useEffect(() => {
    if (!enabled || pendingKey === '' || isFetchingSchedules) return undefined;
    if (retriedKeysRef.current.has(pendingKey)) return undefined;
    const timer = setTimeout(() => {
      retriedKeysRef.current.add(pendingKey);
      setDispatchTick((tick) => tick + 1);
    }, GUIDE_DISPATCH_RETRY_MS);
    return () => clearTimeout(timer);
  }, [enabled, pendingKey, isFetchingSchedules]);

  /* Request records are dropped on unmount (they would otherwise pile up in the
     requests slice for the whole session). */
  useEffect(() => () => {
    dispatch(deleteMultipleRequests(requestIdsRef.current));
  }, [dispatch]);

  /* The "now" resolution only has to stay fresh while the panel is on screen:
     a closed guide stops ticking (no 30s store-wide row rebuild behind live
     playback), and the reopen below refreshes it on the spot. */
  useEffect(() => {
    if (!visible) return undefined;
    const timer = setInterval(() => setNowBucket(Date.now()), GUIDE_REFRESH_MS);
    return () => clearInterval(timer);
  }, [visible]);

  const filters = useMemo(
    () => (filtersMiniEpg
      ? filtersMiniEpg.valueSeq().map((filter) => ({ id: filter.id, name: filter.name })).toArray()
      : []),
    [filtersMiniEpg],
  );
  const emptyMessage = useMemo(() => {
    const messages = selectedFilter?.emptyMessages;
    if (!messages) return undefined;
    return {
      title: messages.title ?? '',
      description: messages.description ?? '',
    };
  }, [selectedFilter]);

  /* Refs keep every handler stable, so the lego's mount identity (and therefore
     the player itself) never changes when the guide data updates. */
  const rowsRef = useRef(rows);
  rowsRef.current = rows;
  const channelListRef = useRef(channelList);
  channelListRef.current = channelList;
  const channelCountRef = useRef(channelCount);
  channelCountRef.current = channelCount;
  /* Set by onSelectChannel, consumed by the route's next mount: "the player is
     remounting because the user zapped from the guide — open the guide again". */
  const reopenGuideRef = useRef(false);
  const labels = useMemo(() => ({
    watching: formatMessage({ id: 'miniEpgScheduleCardWatching.title' }),
    missing: formatMessage({ id: 'scheduleMiniEpg.missing.metadata' }),
    blackout: formatMessage({ id: 'live.blackout.short' }),
    unavailable: formatMessage({ id: 'schedule.missing.metadata' }),
  }), [formatMessage]);
  const labelsRef = useRef(labels);
  labelsRef.current = labels;

  const onOpenGuide = useCallback(() => {
    setEnabled(true);
    setVisible(true);
    /* Re-resolve "what's on now" on the spot (the panel may have been closed
       across a program boundary). */
    setNowBucket(Date.now());
  }, []);

  const onCloseGuide = useCallback(() => {
    setVisible(false);
  }, []);

  /* The lego reports how far its list window can reach; schedules are fetched
     for [0, endIndex) only. Stable identity (ref read) — an unstable callback
     here would remount the player through the shell's effect deps. */
  const onRange = useCallback((endIndex) => {
    const bounded = Math.max(
      GUIDE_INITIAL_RANGE,
      Math.min(channelCountRef.current || endIndex, endIndex),
    );
    setRangeEnd((current) => (current >= bounded ? current : bounded));
  }, []);

  /* "Loading" for the panel: the filter/carousel resolution (useSelectedEpgFilter
     reports fetching until the filter itself resolves) or a schedule request in
     flight. A filter that resolves to nothing is NOT loading — the panel shows
     that filter's empty message instead of skeletons. */
  const isFetchingGuide = isFetching || isFetchingSchedules;

  const snapshot = useMemo(() => ({
    rows,
    filters,
    selectedFilterId: selectedFilter?.id,
    emptyMessage,
    isLoading: isFetchingGuide,
  }), [rows, filters, selectedFilter, emptyMessage, isFetchingGuide]);
  const snapshotRef = useRef(snapshot);
  snapshotRef.current = snapshot;

  const getGuide = useCallback(() => snapshotRef.current, []);
  const getGuideLabels = useCallback(() => labelsRef.current, []);

  /* Ref indirection for the filter hook's callback: the guide API must keep a
     STABLE identity for the whole player lifetime (the shell's mount effect
     depends on it), so nothing here may take a dependency the hook could ever
     recreate. */
  const selectFilterRef = useRef(handleFilterSelect);
  selectFilterRef.current = handleFilterSelect;
  const onSelectFilter = useCallback((filterId) => {
    selectFilterRef.current(filterId);
  }, []);

  const onSelectChannel = useCallback((channelId) => {
    const row = rowsRef.current.find((item) => item.channelId === channelId);
    const channel = channelListRef.current?.find((item) => item.channelId === channelId);
    /* The old mini EPG ignores the channel already playing and blackout
       programs. */
    if (!channel || !row || row.isCurrentChannel || row.isBlackout) return;
    /* The switch is a route replace, so the player remounts: flag it so the new
       instance re-opens the guide (the shell's channel/filter state survives —
       only the solid tree is disposed), and the user can keep zapping. */
    reopenGuideRef.current = true;
    redirectToPlayer(channel, {
      replace: true,
      page: PLAY_SOURCE_PAGE.PLAYER,
      section: PLAY_SOURCE_SECTION.LIVE_MINI_EPG,
      query: wantsHomeOnBack ? { homeOnBack: true } : undefined,
    });
  }, [wantsHomeOnBack]);

  /* Read-and-clear, called by the route when it mounts the player. */
  const consumeReopenGuide = useCallback(() => {
    const value = reopenGuideRef.current;
    reopenGuideRef.current = false;
    return value;
  }, []);

  /* Stable API object: safe to keep in the mount effect's dependency list. */
  return useMemo(() => ({
    getGuide,
    getGuideLabels,
    onOpenGuide,
    onCloseGuide,
    onRange,
    onSelectFilter,
    onSelectChannel,
    consumeReopenGuide,
  }), [
    getGuide,
    getGuideLabels,
    onOpenGuide,
    onCloseGuide,
    onRange,
    onSelectFilter,
    onSelectChannel,
    consumeReopenGuide,
  ]);
}
